import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, Send, Archive, ChevronLeft, Save, Bold, Italic, Underline, List, ListOrdered, Quote, Link2, Image, Tag, X, Sparkles, FileText, Globe, Settings, CheckCircle } from "lucide-react";

export default function AdminBlogsTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: "", content: "", excerpt: "", category: "General", status: "draft", meta_title: "", meta_description: "", featured_image: "" });
  const [blogEditorMode, setBlogEditorMode] = useState("list");
  const [blogViewMode, setBlogViewMode] = useState("visual");
  const [blogTagInput, setBlogTagInput] = useState("");
  const [blogTags, setBlogTags] = useState([]);
  const [showBlogSEO, setShowBlogSEO] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const blogEditorRef = useRef(null);

  useEffect(() => { loadBlogs(); }, []);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const res = await credFetch(`${apiUrl}/api/admin/blogs`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setBlogs(data.blogs);
    } catch (error) { console.error("Blogs error:", error); }
    setLoading(false);
  };

  const saveBlog = async () => {
    if (!blogForm.title.trim()) { toast.error("Please enter a title"); return; }
    try {
      const url = editingBlog ? `${apiUrl}/api/admin/blogs/${editingBlog}` : `${apiUrl}/api/admin/blogs`;
      const method = editingBlog ? "PUT" : "POST";
      const res = await credFetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify({ ...blogForm, tags: blogTags }) });
      if (res.ok) { toast.success(editingBlog ? "Blog updated" : "Blog created"); resetBlogEditor(); loadBlogs(); }
    } catch (error) { toast.error("Save failed"); }
  };

  const resetBlogEditor = () => {
    setEditingBlog(null);
    setBlogForm({ title: "", content: "", excerpt: "", category: "General", status: "draft", meta_title: "", meta_description: "", featured_image: "" });
    setBlogTags([]);
    setBlogTagInput("");
    setBlogEditorMode("list");
    setBlogViewMode("visual");
    setShowBlogSEO(false);
  };

  const openBlogEditor = (blog = null) => {
    if (blog) {
      setEditingBlog(blog.blog_id || blog.id);
      setBlogForm({ title: blog.title || "", content: blog.content || "", excerpt: blog.excerpt || "", category: blog.category || "General", status: blog.status || "draft", meta_title: blog.meta_title || "", meta_description: blog.meta_description || "", featured_image: blog.featured_image || "" });
      setBlogTags(blog.tags || []);
    } else { resetBlogEditor(); }
    setBlogEditorMode("editor");
  };

  const formatBlogText = (command, value = null) => {
    document.execCommand(command, false, value);
    if (blogEditorRef.current) setBlogForm({ ...blogForm, content: blogEditorRef.current.innerHTML });
  };

  const addBlogTag = () => {
    if (blogTagInput.trim() && !blogTags.includes(blogTagInput.trim())) {
      setBlogTags([...blogTags, blogTagInput.trim()]);
      setBlogTagInput("");
    }
  };

  const getAiSuggestions = async (type = "outline") => {
    if (!blogForm.title.trim()) { toast.error("Enter a topic/title first"); return; }
    setAiSuggesting(true);
    try {
      const res = await credFetch(`${apiUrl}/api/blogs/ai/suggest?topic=${encodeURIComponent(blogForm.title)}&content_type=${type}`);
      const data = await res.json();
      if (data.success) { setAiSuggestions({ type, data: data.suggestion }); toast.success("AI suggestions ready!"); }
      else toast.error(data.error || "Failed to get suggestions");
    } catch (error) { toast.error("AI suggestion failed"); }
    setAiSuggesting(false);
  };

  const applyAiSuggestion = (suggestion) => {
    if (typeof suggestion === "string") {
      setBlogForm({ ...blogForm, content: (blogForm.content || "") + "\n\n" + suggestion });
    } else if (suggestion.headings) {
      let content = `<h1>${suggestion.title || blogForm.title}</h1>\n<p>${suggestion.intro_hook || ""}</p>\n\n`;
      suggestion.headings.forEach((h) => { content += `<h2>${h}</h2>\n<p>...</p>\n\n`; });
      setBlogForm({ ...blogForm, title: suggestion.title || blogForm.title, content });
    }
    setAiSuggestions(null);
    toast.success("Applied to editor!");
  };

  const publishBlog = async (id) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/blogs/${id}`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", body: JSON.stringify({ status: "published" }) });
      if (res.ok) { toast.success("Blog published"); loadBlogs(); }
    } catch (error) { toast.error("Publish failed"); }
  };

  const unpublishBlog = async (id) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/blogs/${id}`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", body: JSON.stringify({ status: "draft" }) });
      if (res.ok) { toast.success("Blog unpublished"); loadBlogs(); }
    } catch (error) { toast.error("Failed"); }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      const res = await credFetch(`${apiUrl}/api/admin/blogs/${id}`, { method: "DELETE", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      if (res.ok) { toast.success("Blog deleted"); loadBlogs(); }
    } catch (error) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-blogs-tab">
      {blogEditorMode === "list" ? (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Blog Manager</h2>
            <Button onClick={() => openBlogEditor()} className="bg-violet-600 hover:bg-violet-700" data-testid="new-blog-button"><Plus className="w-4 h-4 mr-2" /> New Blog</Button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white"><CardContent className="p-5"><p className="text-violet-100 text-sm">Total Blogs</p><p className="text-3xl font-bold">{blogs.length}</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white"><CardContent className="p-5"><p className="text-green-100 text-sm">Published</p><p className="text-3xl font-bold">{blogs.filter((b) => b.status === "published").length}</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white"><CardContent className="p-5"><p className="text-amber-100 text-sm">Drafts</p><p className="text-3xl font-bold">{blogs.filter((b) => b.status === "draft").length}</p></CardContent></Card>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead>Views</TableHead><TableHead>Date</TableHead><TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blogs.map((blog) => (
                  <TableRow key={blog.id || blog.blog_id}>
                    <TableCell className="font-medium max-w-xs truncate">{blog.title}</TableCell>
                    <TableCell><Badge variant="outline">{blog.category}</Badge></TableCell>
                    <TableCell><Badge className={blog.status === "published" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>{blog.status}</Badge></TableCell>
                    <TableCell>{blog.views || 0}</TableCell>
                    <TableCell className="text-sm text-gray-500">{blog.created_at ? new Date(blog.created_at).toLocaleDateString() : "N/A"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openBlogEditor(blog)} title="Edit"><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => window.open(`/blog/${blog.slug}`, "_blank")} title="View"><Eye className="w-4 h-4" /></Button>
                        {blog.status === "draft" ? (
                          <Button size="sm" variant="ghost" className="text-green-600" onClick={() => publishBlog(blog.id || blog.blog_id)} title="Publish"><Send className="w-4 h-4" /></Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => unpublishBlog(blog.id || blog.blog_id)} title="Unpublish"><Archive className="w-4 h-4" /></Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteBlog(blog.id || blog.blog_id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {blogs.length === 0 && <div className="text-center py-12 text-gray-500">No blogs yet. Click "New Blog" to create one.</div>}
          </Card>
        </>
      ) : (
        <>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={resetBlogEditor} data-testid="blog-back-button"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <h2 className="text-xl font-bold" data-testid="blog-editor-title">{editingBlog ? "Edit Blog" : "New Blog"}</h2>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={blogForm.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>{blogForm.status === "published" ? "Published" : "Draft"}</Badge>
              <Button variant="outline" onClick={saveBlog} data-testid="save-draft-button"><Save className="w-4 h-4 mr-2" /> Save Draft</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setBlogForm({ ...blogForm, status: "published" }); saveBlog(); }} data-testid="publish-button"><Send className="w-4 h-4 mr-2" /> Publish</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-4">
              <Input placeholder="Enter blog title..." value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} className="text-xl font-bold h-12" data-testid="blog-title-input" />
              <Card data-testid="blog-editor-card">
                <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border-b" data-testid="blog-toolbar">
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("bold")} title="Bold" data-testid="toolbar-bold"><Bold className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("italic")} title="Italic" data-testid="toolbar-italic"><Italic className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("underline")} title="Underline" data-testid="toolbar-underline"><Underline className="w-4 h-4" /></Button>
                  <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("formatBlock", "h1")} title="H1" data-testid="toolbar-h1">H1</Button>
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("formatBlock", "h2")} title="H2" data-testid="toolbar-h2">H2</Button>
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("formatBlock", "h3")} title="H3" data-testid="toolbar-h3">H3</Button>
                  <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("insertUnorderedList")} title="Bullet List" data-testid="toolbar-list"><List className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("insertOrderedList")} title="Numbered List" data-testid="toolbar-list-ordered"><ListOrdered className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => formatBlogText("formatBlock", "blockquote")} title="Quote" data-testid="toolbar-quote"><Quote className="w-4 h-4" /></Button>
                  <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
                  <Button size="sm" variant="ghost" onClick={() => { const url = prompt("Enter URL:"); if (url) formatBlogText("createLink", url); }} title="Link" data-testid="toolbar-link"><Link2 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => { const url = prompt("Enter image URL:"); if (url) formatBlogText("insertImage", url); }} title="Image" data-testid="toolbar-image"><Image className="w-4 h-4" /></Button>
                  <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
                  <Button size="sm" variant={blogViewMode === "visual" ? "default" : "ghost"} onClick={() => setBlogViewMode("visual")} data-testid="view-mode-visual">Visual</Button>
                  <Button size="sm" variant={blogViewMode === "html" ? "default" : "ghost"} onClick={() => setBlogViewMode("html")} data-testid="view-mode-html">HTML</Button>
                  <Button size="sm" variant={blogViewMode === "preview" ? "default" : "ghost"} onClick={() => setBlogViewMode("preview")} data-testid="view-mode-preview">Preview</Button>
                  <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
                  <div className="relative group">
                    <Button size="sm" variant="outline" className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100" disabled={aiSuggesting} data-testid="ai-suggest-btn">
                      <Sparkles className="w-4 h-4 mr-1" />{aiSuggesting ? "Thinking..." : "AI Assist"}
                    </Button>
                    <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[160px]">
                      <button onClick={() => getAiSuggestions("outline")} className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2"><FileText className="w-4 h-4" /> Generate Outline</button>
                      <button onClick={() => getAiSuggestions("intro")} className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2"><Edit className="w-4 h-4" /> Write Intro</button>
                      <button onClick={() => getAiSuggestions("conclusion")} className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Write Conclusion</button>
                      <button onClick={() => getAiSuggestions("ideas")} className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Topic Ideas</button>
                    </div>
                  </div>
                </div>
                {aiSuggestions && (
                  <div className="p-4 bg-purple-50 border-b border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-purple-800 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Suggestions</span>
                      <Button size="sm" variant="ghost" onClick={() => setAiSuggestions(null)}><X className="w-4 h-4" /></Button>
                    </div>
                    {aiSuggestions.type === "outline" && typeof aiSuggestions.data === "object" && (
                      <div className="space-y-2">
                        {aiSuggestions.data.title && <p className="text-sm"><strong>Title:</strong> {aiSuggestions.data.title}</p>}
                        {aiSuggestions.data.headings && (<div><p className="text-sm font-medium mb-1">Suggested Headings:</p><ul className="text-sm list-disc pl-5 space-y-1">{aiSuggestions.data.headings.map((h, i) => <li key={i}>{h}</li>)}</ul></div>)}
                        {aiSuggestions.data.intro_hook && <p className="text-sm"><strong>Hook:</strong> {aiSuggestions.data.intro_hook}</p>}
                        <Button size="sm" className="mt-2 bg-purple-600 hover:bg-purple-700" onClick={() => applyAiSuggestion(aiSuggestions.data)}>Apply Outline</Button>
                      </div>
                    )}
                    {aiSuggestions.type === "ideas" && Array.isArray(aiSuggestions.data) && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Related Topic Ideas:</p>
                        {aiSuggestions.data.map((idea, i) => (
                          <div key={i} className="p-2 bg-white rounded border cursor-pointer hover:border-purple-400" onClick={() => setBlogForm({ ...blogForm, title: idea.topic })}>
                            <p className="font-medium text-sm">{idea.topic}</p>
                            <p className="text-xs text-slate-500">{idea.angle}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${idea.difficulty === "easy" ? "bg-green-100 text-green-700" : idea.difficulty === "hard" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{idea.difficulty}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(aiSuggestions.type === "intro" || aiSuggestions.type === "conclusion") && typeof aiSuggestions.data === "string" && (
                      <div>
                        <p className="text-sm font-medium mb-2">{aiSuggestions.type === "intro" ? "Introduction:" : "Conclusion:"}</p>
                        <p className="text-sm whitespace-pre-wrap bg-white p-3 rounded border">{aiSuggestions.data}</p>
                        <Button size="sm" className="mt-2 bg-purple-600 hover:bg-purple-700" onClick={() => applyAiSuggestion(aiSuggestions.data)}>Add to Content</Button>
                      </div>
                    )}
                    {typeof aiSuggestions.data === "string" && !["intro", "conclusion"].includes(aiSuggestions.type) && (
                      <div>
                        <p className="text-sm whitespace-pre-wrap">{aiSuggestions.data}</p>
                        <Button size="sm" className="mt-2 bg-purple-600 hover:bg-purple-700" onClick={() => applyAiSuggestion(aiSuggestions.data)}>Add to Content</Button>
                      </div>
                    )}
                  </div>
                )}
                {blogViewMode === "visual" && (
                  <div ref={blogEditorRef} contentEditable className="min-h-[400px] p-4 prose prose-lg max-w-none focus:outline-none" dangerouslySetInnerHTML={{ __html: blogForm.content }} onInput={(e) => setBlogForm({ ...blogForm, content: e.currentTarget.innerHTML })} onBlur={(e) => setBlogForm({ ...blogForm, content: e.currentTarget.innerHTML })} data-testid="blog-content-editor" />
                )}
                {blogViewMode === "html" && (
                  <Textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} className="min-h-[400px] font-mono text-sm border-0 rounded-none" placeholder="<p>Enter HTML content...</p>" data-testid="blog-content-html" />
                )}
                {blogViewMode === "preview" && (
                  <div className="min-h-[400px] p-6" data-testid="blog-content-preview">
                    <h1 className="text-3xl font-bold mb-4">{blogForm.title || "Untitled"}</h1>
                    <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: blogForm.content || '<p class="text-gray-400">No content yet...</p>' }} />
                  </div>
                )}
                <div className="px-4 py-2 bg-slate-50 border-t text-sm text-slate-500" data-testid="word-count">
                  {blogForm.content.replace(/<[^>]*>/g, "").split(/\s+/).filter((w) => w).length} words
                </div>
              </Card>
              <Card>
                <CardHeader className="py-3"><CardTitle className="text-sm">Excerpt / Summary</CardTitle></CardHeader>
                <CardContent><Textarea placeholder="Write a short summary..." value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} rows={3} /></CardContent>
              </Card>
            </div>
            <div className="space-y-4">
              <Card>
                <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Settings className="w-4 h-4" /> Settings</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><label className="text-sm font-medium">Status</label><Select value={blogForm.status} onValueChange={(v) => setBlogForm({ ...blogForm, status: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent></Select></div>
                  <div><label className="text-sm font-medium">Category</label><Select value={blogForm.category} onValueChange={(v) => setBlogForm({ ...blogForm, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{["General", "SEO", "Marketing", "Social Media", "Content", "YouTube", "Tutorials"].map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent></Select></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4" /> Tags</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-2"><Input placeholder="Add tag..." value={blogTagInput} onChange={(e) => setBlogTagInput(e.target.value)} onKeyPress={(e) => e.key === "Enter" && addBlogTag()} className="flex-1" /><Button size="sm" onClick={addBlogTag}><Plus className="w-4 h-4" /></Button></div>
                  <div className="flex flex-wrap gap-2">{blogTags.map((tag, idx) => <Badge key={idx} variant="secondary" className="gap-1">{tag}<X className="w-3 h-3 cursor-pointer" onClick={() => setBlogTags(blogTags.filter((t) => t !== tag))} /></Badge>)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Image className="w-4 h-4" /> Featured Image</CardTitle></CardHeader>
                <CardContent>
                  {blogForm.featured_image ? (
                    <div className="relative"><img src={blogForm.featured_image} alt="" className="w-full h-32 object-cover rounded" /><Button size="sm" variant="destructive" className="absolute top-2 right-2" onClick={() => setBlogForm({ ...blogForm, featured_image: "" })}><Trash2 className="w-4 h-4" /></Button></div>
                  ) : <Input placeholder="Enter image URL..." onChange={(e) => setBlogForm({ ...blogForm, featured_image: e.target.value })} />}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-3 cursor-pointer" onClick={() => setShowBlogSEO(!showBlogSEO)}>
                  <CardTitle className="text-sm flex items-center justify-between"><span className="flex items-center gap-2"><Globe className="w-4 h-4" /> SEO Settings</span><Badge variant="outline">{showBlogSEO ? "Hide" : "Show"}</Badge></CardTitle>
                </CardHeader>
                {showBlogSEO && (
                  <CardContent className="space-y-3">
                    <div><label className="text-sm font-medium">Meta Title</label><Input placeholder="SEO title (60 chars)" value={blogForm.meta_title} onChange={(e) => setBlogForm({ ...blogForm, meta_title: e.target.value })} maxLength={60} /></div>
                    <div><label className="text-sm font-medium">Meta Description</label><Textarea placeholder="SEO description (160 chars)" value={blogForm.meta_description} onChange={(e) => setBlogForm({ ...blogForm, meta_description: e.target.value })} maxLength={160} rows={3} /></div>
                  </CardContent>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
